import { useQuery } from "@apollo/client";
import { createStyles, Grid, makeStyles, RadioGroup, Tooltip } from "@material-ui/core";
import Autocomplete from "components/Autocomplete";
import TextField from "components/TextField";
import i18n from "config/i18n";
import useText from "constants/text";
import { ProductContext } from "contexts/ProductContext";
import { allCategoriesQuery } from "domains/Catalog/graphql/queries";
import {
  ParcelRadio,
  ParcelRadioL,
  ParcelRadioM,
  ParcelRadioXL,
  ParcelRadioXXL,
} from "domains/Task/components/Job/Radio";
import { Category, Product } from "generated/graphql";
import { SizedIcon } from "icons";
import React from "react";

const useStyles = makeStyles(() =>
  createStyles({
    group: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    marginTop: {
      marginTop: 20,
    },
  })
);

export function CategoryForm() {
  const styles = useStyles();
  const { product, setProduct, error, setError } = React.useContext(ProductContext);
  const text = useText();
  const { data, loading } = useQuery(allCategoriesQuery);
  const onChange = (e: any) => {
    setError((prev: any) => ({ ...prev, submitted: false }));
    e.persist();
    setProduct((prev: Product) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const lng = React.useMemo(
    () => (i18n.language.toLocaleUpperCase() === "FR" ? 0 : 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );
  return (
    <Grid container md={12} spacing={2} item>
      <Grid item xs={6}>
        <Autocomplete
          fullWidth
          label={text.text.all.category}
          variant="outlined"
          name="category"
          options={data?.allCategories?.data?.categories || []}
          getOptionLabel={(option: Category) =>
            option?.translations != null ? option?.translations[lng]?.text : "loading ..."
          }
          value={product.category}
          getOptionSelected={(option: Category, selection: Category) => option?.code === selection?.code}
          onChange={(e: any, value: Category) => setProduct((prev: Product) => ({ ...prev, category: value }))}
          loading={loading}
          error={error.category.error && error.submitted}
          helperText={error.category.error && error.submitted && text.text.all.required_field}
          required={error.category.required}
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          fullWidth
          label={text.text.all.value}
          variant="outlined"
          inputProps={{
            min: 25,
          }}
          name="value"
          onChange={onChange}
          value={product.value}
          type="number"
          required={error.value.required}
          error={error.value.error && error.submitted}
          helperText={
            error.value.error && error.submitted && `${text.text.all.required_field} And equal or gretter than 25`
          }
        />
      </Grid>
      <Grid item xs={6} className={styles.marginTop}>
        <TextField
          fullWidth
          label="Volume"
          variant="outlined"
          inputProps={{
            min: 0,
          }}
          name="volume"
          onChange={onChange}
          value={product.volume}
          type="number"
          placeholder="Renseigner le volume"
        />
      </Grid>
      <Grid item xs={6}>
        <RadioGroup
          row
          aria-label="Weight"
          className={styles.group}
          name="weight"
          value={product.weight}
          onChange={onChange}
        >
          <Tooltip title="S (max 5kg) ex: Laptop, cell phone, cloth, soap jar">
            <ParcelRadio value="S" icon={<SizedIcon />} checkedIcon={<SizedIcon color="#399ebf" />} />
          </Tooltip>
          <Tooltip title="M (max 30kg) ex: Small electrical appliances (microwave, fridge bar, cardboard, TV)">
            <ParcelRadioM value="M" icon={<SizedIcon />} checkedIcon={<SizedIcon color="#399ebf" />} />
          </Tooltip>
          <Tooltip title="L (max 60kg) ex: Big electrical appliances (fridge, gas cooker, washing machine)">
            <ParcelRadioL value="L" icon={<SizedIcon />} checkedIcon={<SizedIcon color="#399ebf" />} />
          </Tooltip>
          <Tooltip title="XL (max 100kg) ex: Bedroom">
            <ParcelRadioXL value="XL" icon={<SizedIcon />} checkedIcon={<SizedIcon color="#399ebf" />} />
          </Tooltip>
          <Tooltip title="XL (max 200kg) ex: Car">
            <ParcelRadioXXL value="XXL" icon={<SizedIcon />} checkedIcon={<SizedIcon color="#399ebf" />} />
          </Tooltip>
        </RadioGroup>
      </Grid>
    </Grid>
  );
}
